/**
 * 텍스트 안의 URL을 찾아 표시용 조각으로 나누는 유틸. (DAM-858)
 *
 * 설명이나 커스텀 메타데이터(string) 값에 주소가 섞여 있어도 화면에서는 그냥 글자라
 * 바로 열어볼 수 없었다. 표시 단계에서만 링크로 바꿔 주기 위해 값을 text/link 조각으로
 * 쪼갠다 — 저장된 값 자체는 건드리지 않는다.
 *
 * 링크로 인정하는 것은 `http://` `https://` 로 시작하는 토큰뿐이다. `www.` 로 시작하는
 * 문자열까지 링크로 보면 프로토콜을 임의로 붙여야 하고, 무엇보다 링크가 아닌 일반 텍스트가
 * 링크가 되는 오검출이 값을 잘못 보여주는 쪽이라 더 나쁘다.
 */

/** 텍스트에서 URL 후보를 뽑는다. 공백과 따옴표/꺾쇠는 주소에 올 수 없으므로 경계로 쓴다. */
const URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;

/** 문장 끝에 붙어 딸려 들어오기 쉬운 문장부호 — 주소에 포함하면 링크가 깨진다. */
const TRAILING_PUNCTUATION_CHARS = new Set([".", ",", ";", ":", "!", "?", ")", "]", "}", "'", '"']);

/** 축약 없이 그대로 보여 줄 라벨의 최대 길이. */
export const DEFAULT_LINK_LABEL_MAX_LENGTH = 40;

/**
 * `(https://example.com)` 이나 `https://example.com.` 처럼 주소 뒤에 붙은 문장부호를 떼어낸다.
 * 다만 주소 안에 여는 괄호가 있으면(`.../Foo_(bar)`) 닫는 괄호는 주소의 일부이므로 남긴다.
 *
 * 괄호 개수는 처음에 한 번만 세고 떼어내면서 갱신한다 — 한 글자 뗄 때마다 다시 세면
 * 문장부호가 길게 이어진 값에서 O(n^2)이 된다.
 */
const trimTrailingPunctuation = (raw) => {
    let openCount = 0;
    let closeCount = 0;

    for (const char of raw) {
        if (char === "(") openCount += 1;
        else if (char === ")") closeCount += 1;
    }

    let end = raw.length;

    while (end > 0 && TRAILING_PUNCTUATION_CHARS.has(raw[end - 1])) {
        if (raw[end - 1] === ")") {
            if (openCount >= closeCount) break;
            closeCount -= 1;
        }
        end -= 1;
    }

    return raw.slice(0, end);
};

/**
 * `https://a.com,https://b.com` 처럼 구분자만 두고 다음 주소가 이어 붙은 경우 그 앞에서 끊는다.
 * 콤마는 쿼리스트링에 정상적으로 쓰이므로 문자 후보에서 빼지 않고, 프로토콜이 다시 나오는
 * 지점만 경계로 본다.
 */
const cutAtNextProtocol = (raw) => {
    const offset = raw.slice(1).search(/https?:\/\//i);
    return offset < 0 ? raw : raw.slice(0, offset + 1);
};

/** 열 수 있는 http(s) 주소면 URL 객체를, 아니면 null 을 준다. */
const toUrl = (href) => {
    try {
        const url = new URL(href);
        return ["http:", "https:"].includes(url.protocol) ? url : null;
    } catch {
        return null;
    }
};

/**
 * 텍스트를 링크와 일반 텍스트 조각으로 나눈다. 링크가 하나도 없으면 텍스트 조각 하나만 나온다.
 *
 * @param {*} text 표시할 값. 문자열이 아니면 빈 배열을 준다.
 * @returns {Array<{type: "text"|"link", value: string, href?: string}>}
 */
export const parseTextLinks = (text) => {
    if (typeof text !== "string" || text === "") return [];

    const segments = [];
    // 토큰을 짧게 자르면 그 뒤를 다시 훑어야 하므로 lastIndex 를 직접 조정한다.
    const pattern = new RegExp(URL_PATTERN.source, "gi");
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
        const href = trimTrailingPunctuation(cutAtNextProtocol(match[0]));

        // 잘라낸 나머지(이어 붙은 다음 주소)를 다음 회차에서 다시 본다.
        pattern.lastIndex = Math.max(match.index + href.length, match.index + 1);

        // 주소로 읽히지 않으면 링크로 만들지 않고 일반 텍스트로 남긴다.
        if (!toUrl(href)) continue;

        if (match.index > lastIndex) segments.push({ type: "text", value: text.slice(lastIndex, match.index) });
        segments.push({ type: "link", value: href, href });
        lastIndex = match.index + href.length;
    }

    if (lastIndex < text.length) segments.push({ type: "text", value: text.slice(lastIndex) });

    return segments;
};

/**
 * 주소를 화면에 보여 줄 짧은 라벨로 만든다. 프로토콜과 `www.` 는 정보가 없어 떼고,
 * 기준 길이를 넘으면 뒤를 잘라 앞쪽(도메인과 경로 앞부분)이 남게 한다.
 *
 * @param {string} href
 * @param {number} [maxLength=DEFAULT_LINK_LABEL_MAX_LENGTH]
 * @returns {string}
 */
export const getLinkLabel = (href, maxLength = DEFAULT_LINK_LABEL_MAX_LENGTH) => {
    if (!href) return "";

    const stripped = href
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "")
        .replace(/\/$/, "");

    return stripped.length > maxLength ? `${stripped.slice(0, maxLength)}…` : stripped;
};

/**
 * 주소가 속한 사이트의 파비콘 경로. 외부 파비콘 서비스를 거치지 않고 해당 도메인에 직접 요청하므로,
 * 파비콘이 없는 사이트에서는 실패한다 — 호출부가 로드 실패를 폴백 아이콘으로 받아야 한다.
 *
 * @param {string} href
 * @returns {string|null} 주소로 읽히지 않으면 null
 */
export const getFaviconUrl = (href) => {
    const url = toUrl(href);
    return url ? `${url.origin}/favicon.ico` : null;
};
